import { useEffect, useRef, useState } from "react";
import { embedSvgFonts } from "@/lib/svgFontEmbed";
import { rasterizeSvgToPng, svgViewBoxSize } from "@/lib/svgRasterize";

interface SvgPngExportPanelProps {
  /** Fully substituted SVG source. The panel embeds fonts itself before
   *  rasterizing / downloading, so callers pass the raw substituted source. */
  svg: string;
  /** Filename stem for downloads (no extension). */
  fileBase: string;
}

const SCALES = [1, 2, 4] as const;
/** Server rejects dimensions outside 1..=8192. */
const MAX_DIM = 8192;

/**
 * The "render" home for SVG projects. PNG export is the only raster format
 * (transparent background, from the server-side Chromium rasterizer so
 * embedded fonts paint identically to the preview). Any output size goes:
 * the W/H inputs are aspect-linked to the viewBox — edit one and the other
 * follows; the scale buttons are just presets that fill both. Also offers a
 * font-embedded .svg download so the Render tab is the single export home.
 *
 * Owns all its own state (size / busy / error) so hosting it in a tab pane
 * never adds hooks above a parent's early returns.
 */
export function SvgPngExportPanel({ svg, fileBase }: SvgPngExportPanelProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dims = svg ? svgViewBoxSize(svg) : null;
  const aspect = dims && dims.height > 0 ? dims.width / dims.height : 1;

  const [width, setWidth] = useState<number>(dims ? dims.width * 2 : 1024);
  const [height, setHeight] = useState<number>(
    dims ? dims.height * 2 : 1024,
  );

  // Re-seed the inputs when the underlying document's viewBox changes
  // (new session content, edited canvas) — but not on every keystroke of
  // the svg source: only when the RATIO or base size actually changed.
  const seededForRef = useRef<string>("");
  useEffect(() => {
    if (!dims) return;
    const sig = `${dims.width}x${dims.height}`;
    if (seededForRef.current === sig) return;
    seededForRef.current = sig;
    setWidth(clampDim(Math.round(dims.width * 2)));
    setHeight(clampDim(Math.round(dims.height * 2)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dims?.width, dims?.height]);

  function clampDim(v: number): number {
    return Math.max(1, Math.min(MAX_DIM, v));
  }

  /** Linked scaling: editing one dimension recomputes the other from the
   *  viewBox aspect ratio. */
  const onWidth = (raw: number) => {
    const w = clampDim(Math.round(raw) || 1);
    setWidth(w);
    setHeight(clampDim(Math.round(w / aspect)));
  };
  const onHeight = (raw: number) => {
    const h = clampDim(Math.round(raw) || 1);
    setHeight(h);
    setWidth(clampDim(Math.round(h * aspect)));
  };
  const applyScale = (s: number) => {
    if (!dims) return;
    setWidth(clampDim(Math.round(dims.width * s)));
    setHeight(clampDim(Math.round(dims.height * s)));
  };
  const activeScale = (s: number) =>
    dims &&
    width === clampDim(Math.round(dims.width * s)) &&
    height === clampDim(Math.round(dims.height * s));

  const exportPng = async () => {
    if (!svg || busy) return;
    if (!dims) {
      setError("This SVG has no viewBox, so an output size can't be computed.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const embedded = await embedSvgFonts(svg);
      const blob = await rasterizeSvgToPng(embedded, width, height);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileBase}-${width}x${height}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "PNG export failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadSvg = async () => {
    if (!svg) return;
    const embedded = await embedSvgFonts(svg);
    const url = URL.createObjectURL(
      new Blob([embedded], { type: "image/svg+xml" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileBase}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const inputClasses =
    "w-24 rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-line-strong focus:outline-none disabled:opacity-50";

  return (
    <div className="flex w-full max-w-[420px] flex-col gap-5 text-xs text-ink">
      {/* Format — PNG is the only raster format for SVG projects. */}
      <section className="flex flex-col gap-2">
        <label className="text-xs font-medium text-ink-2">Format</label>
        <div className="rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink">
          PNG (transparent)
        </div>
      </section>

      {/* Output size — free W/H, aspect-linked to the viewBox. */}
      <section className="flex flex-col gap-2">
        <label className="text-xs font-medium text-ink-2">Output size</label>
        {dims ? (
          <>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={MAX_DIM}
                value={width}
                onChange={(e) => onWidth(Number(e.target.value))}
                aria-label="PNG width"
                className={inputClasses}
              />
              <span className="text-[10px] text-ink-3">×</span>
              <input
                type="number"
                min={1}
                max={MAX_DIM}
                value={height}
                onChange={(e) => onHeight(Number(e.target.value))}
                aria-label="PNG height"
                className={inputClasses}
              />
              <span className="text-[10px] text-ink-3">px, linked</span>
            </div>
            <div className="flex gap-1.5">
              {SCALES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => applyScale(s)}
                  className={`rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors ${
                    activeScale(s)
                      ? "bg-accent-soft text-accent"
                      : "bg-surface-3 text-ink-2 hover:text-ink"
                  }`}
                >
                  {s}×
                </button>
              ))}
              <span className="self-center text-[10px] text-ink-3">
                of {dims.width} × {dims.height}
              </span>
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink-3">
            No viewBox — output size unavailable.
          </div>
        )}
      </section>

      {/* Export PNG — primary. */}
      <button
        type="button"
        onClick={() => void exportPng()}
        disabled={busy || !svg}
        className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
      >
        {busy ? "Exporting…" : `Export PNG (${width} × ${height})`}
      </button>

      {error && <p className="text-xs text-danger">{error}</p>}

      {/* Download SVG — secondary. Keeps the Render tab the one export home. */}
      <button
        type="button"
        onClick={() => void downloadSvg()}
        disabled={!svg}
        className="rounded-lg border border-line bg-surface px-4 py-2 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
      >
        Download SVG
      </button>
    </div>
  );
}
